import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import UpgradePrompt from '../dashboard/UpgradePrompt';
import { getUserSubscriptions, type Subscription } from '../../api/payment';

interface SubscriptionGuardProps {
    children: React.ReactNode;
    requiredPlan: 'mail' | 'template' | 'bundle'; // Plans that allow access
}

const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({ children, requiredPlan }) => {
    const { user, loading: authLoading } = useAuth();
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
    const [loading, setLoading] = useState(true);
    const hasLoadedRef = useRef(false);

    useEffect(() => {
        if (hasLoadedRef.current) return;
        hasLoadedRef.current = true;

        if (!authLoading && user) {
            const loadSubscriptions = async () => {
                try {
                    const data = await getUserSubscriptions();
                    setSubscriptions(data || []);
                } catch (error) {
                    console.error('Failed to load subscriptions:', error);
                    setSubscriptions([]);
                } finally {
                    setLoading(false);
                }
            };
            loadSubscriptions();
        } else if (!authLoading && !user) {
            setLoading(false);
        }
    }, [authLoading, user]);

    if (authLoading || loading) {
        return <div className="animate-pulse bg-gray-100 h-96 rounded-2xl w-full"></div>;
    }

    // If no user, technically shouldn't be here (protected route), but handle safely
    if (!user) return null;

    // Check if user has active subscription for the required plan
    const hasActiveSubscription = (moduleType: 'mail' | 'template' | 'bundle'): boolean => {
        return subscriptions.some(sub => 
            sub.module_type === moduleType && 
            sub.is_active && 
            (sub.status === 'active' || sub.status === 'trial')
        );
    };

    // "bundle" has access to everything
    if (hasActiveSubscription('bundle')) {
        return <>{children}</>;
    }

    // Check specific module access
    if (requiredPlan === 'mail') {
        if (hasActiveSubscription('mail') || hasActiveSubscription('bundle')) {
            return <>{children}</>;
        }
    } else if (requiredPlan === 'template') {
        if (hasActiveSubscription('template') || hasActiveSubscription('bundle')) {
            return <>{children}</>;
        }
    } else if (requiredPlan === 'bundle') {
        if (hasActiveSubscription('bundle')) {
            return <>{children}</>;
        }
    }

    // No active subscription for required plan
    return <UpgradePrompt requiredPlan={requiredPlan} />;
};

export default SubscriptionGuard;
